/**
 * NavigatorX App Configuration Service
 * 
 * Enterprise-grade configuration management for NavigatorX:
 * - Feature flags with rollout percentages and user targeting
 * - App configuration (trial duration, pricing, rate limits)
 * - Force update management with platform-specific versions
 * - Maintenance mode with bypass lists
 * - Full audit trail and version history
 * 
 * Architecture:
 * - All configs stored in Firestore with real-time sync capability
 * - Configs are cached on client with TTL-based invalidation
 * - Changes are versioned for rollback capability
 * - Audit logging for all modifications
 * 
 * @author Claude AI for TGE.LA
 * @version 1.0.0
 */

import {
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  updateDoc,
  addDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  limit,
  Timestamp,
  onSnapshot,
  writeBatch,
  increment
} from 'firebase/firestore';
import { navDb } from '../firebase/client';
import { logAdminAction } from './navigatorXAuditService';

// ============================================================================
// COLLECTION CONSTANTS
// ============================================================================

const CONFIG_COLLECTION = 'nav_config';
const FEATURE_FLAGS_DOC = 'feature_flags';
const APP_CONFIG_DOC = 'app_config';
const FORCE_UPDATE_DOC = 'force_update';
const MAINTENANCE_DOC = 'maintenance';
const CONFIG_HISTORY_COLLECTION = 'nav_config_history';

// ============================================================================
// TYPE DEFINITIONS
// ============================================================================

/**
 * Feature Flag Definition
 * Supports boolean flags, percentage rollouts, and user targeting
 */
export interface FeatureFlag {
  /** Unique identifier for the flag */
  key: string;
  
  /** Human-readable name */
  name: string;
  
  /** Detailed description of what this flag controls */
  description: string;
  
  /** Whether the flag is globally enabled */
  enabled: boolean;
  
  /** Percentage of users to roll out to (0-100) */
  rolloutPercentage: number;
  
  /** Specific user IDs that always get this flag */
  enabledUserIds: string[];
  
  /** Specific user IDs that never get this flag */
  disabledUserIds: string[];
  
  /** Subscription statuses that get this flag */
  enabledSubscriptionStatuses: string[];
  
  /** Environment (production, staging, development) */
  environment: 'production' | 'staging' | 'development' | 'all';
  
  /** When this flag was created */
  createdAt: Timestamp;
  
  /** When this flag was last modified */
  updatedAt: Timestamp;
  
  /** Who created this flag */
  createdBy: string;
  
  /** Who last modified this flag */
  updatedBy: string;
  
  /** Optional expiration date for the flag */
  expiresAt?: Timestamp;
  
  /** Tags for organization */
  tags: string[];
  
  /** Whether this is a critical flag that requires confirmation */
  isCritical: boolean;
}

/**
 * App Configuration
 * Core application settings
 */
export interface AppConfig {
  /** Trial configuration */
  trial: {
    /** Duration of trial in days */
    durationDays: number;
    /** Whether trials are enabled */
    enabled: boolean;
    /** Features available during trial */
    features: string[];
    /** Whether to show trial expiry warnings */
    showExpiryWarnings: boolean;
    /** Days before expiry to show first warning */
    warningDaysBefore: number;
  };
  
  /** Pricing configuration */
  pricing: {
    /** Monthly subscription price in USD */
    monthlyPrice: number;
    /** Annual subscription price in USD (if offered) */
    annualPrice?: number;
    /** Currency code */
    currency: string;
    /** Whether to show pricing in app */
    displayPricing: boolean;
    /** Promotional message */
    promoMessage?: string;
    /** Promo expiry date */
    promoExpiresAt?: Timestamp;
  };
  
  /** Referral program configuration */
  referral: {
    /** Whether referral program is enabled */
    enabled: boolean;
    /** Free months earned per successful referral */
    freeMonthsPerReferral: number;
    /** Months referee must pay before referrer gets credit */
    requiredPaidMonths: number;
    /** Maximum referrals per user */
    maxReferralsPerUser: number;
    /** Referral code prefix */
    codePrefix: string;
  };
  
  /** Rate limiting configuration */
  rateLimits: {
    /** API requests per minute */
    apiRequestsPerMinute: number;
    /** Well searches per minute */
    wellSearchesPerMinute: number;
    /** Navigation requests per hour */
    navigationsPerHour: number;
    /** Hazard reports per day */
    hazardReportsPerDay: number;
  };
  
  /** API endpoints configuration */
  endpoints: {
    /** OCC API base URL */
    occApiUrl: string;
    /** Whether to use production OCC API */
    useProductionOcc: boolean;
    /** Cache TTL in minutes */
    cacheTtlMinutes: number;
  };
  
  /** Last updated timestamp */
  updatedAt: Timestamp;
  
  /** Last updated by */
  updatedBy: string;
}

/**
 * Force Update Configuration
 * Controls minimum app version requirements
 */
export interface ForceUpdateConfig {
  /** Whether force update is enabled */
  enabled: boolean;
  
  /** Android configuration */
  android: {
    /** Minimum required version code */
    minVersionCode: number;
    /** Minimum required version name (e.g., "1.2.0") */
    minVersionName: string;
    /** Play Store URL */
    storeUrl: string;
    /** Grace period in hours after which update is mandatory */
    gracePeriodHours: number;
    /** Whether to allow skipping this update */
    allowSkip: boolean;
  };
  
  /** iOS configuration (for future use) */
  ios: {
    /** Minimum required build number */
    minBuildNumber: number;
    /** Minimum required version string */
    minVersionString: string;
    /** App Store URL */
    storeUrl: string;
    /** Grace period in hours */
    gracePeriodHours: number;
    /** Whether to allow skipping this update */
    allowSkip: boolean;
  };
  
  /** Update message shown to users */
  updateMessage: {
    title: string;
    body: string;
    /** Optional "What's New" bullet points */
    whatsNew: string[];
  };
  
  /** Last updated */
  updatedAt: Timestamp;
  updatedBy: string;
}

/**
 * Maintenance Mode Configuration
 */
export interface MaintenanceConfig {
  /** Whether maintenance mode is active */
  enabled: boolean;
  
  /** Maintenance message */
  message: {
    title: string;
    body: string;
  };
  
  /** Scheduled start time (for pre-announcing maintenance) */
  scheduledStartTime?: Timestamp;
  
  /** Estimated end time */
  estimatedEndTime?: Timestamp;
  
  /** When maintenance actually started */
  startedAt?: Timestamp;
  
  /** User IDs that can bypass maintenance */
  bypassUserIds: string[];
  
  /** Email domains that can bypass (e.g., @oilwellx.com) */
  bypassEmailDomains: string[];
  
  /** Subscription statuses that can bypass */
  bypassSubscriptionStatuses: string[];
  
  /** Whether to show countdown timer */
  showCountdown: boolean;
  
  /** Whether to allow read-only access during maintenance */
  allowReadOnly: boolean;
  
  /** Last updated */
  updatedAt: Timestamp;
  updatedBy: string;
}

/**
 * Configuration Change History Entry
 */
export interface ConfigHistoryEntry {
  id?: string;
  configType: 'feature_flag' | 'app_config' | 'force_update' | 'maintenance';
  configKey?: string;
  action: 'create' | 'update' | 'delete' | 'enable' | 'disable' | 'rollback';
  previousValue: any;
  newValue: any;
  changedBy: string;
  changedAt: Timestamp;
  reason?: string;
  ipAddress?: string;
}

// ============================================================================
// DEFAULT CONFIGURATIONS
// ============================================================================

const DEFAULT_APP_CONFIG: Omit<AppConfig, 'updatedAt' | 'updatedBy'> = {
  trial: {
    durationDays: 7,
    enabled: true,
    features: ['navigation', 'favorites', 'hazards', 'search', 'voice'],
    showExpiryWarnings: true,
    warningDaysBefore: 2
  },
  pricing: {
    monthlyPrice: 9.99,
    currency: 'USD',
    displayPricing: true
  },
  referral: {
    enabled: true,
    freeMonthsPerReferral: 1,
    requiredPaidMonths: 2,
    maxReferralsPerUser: 50,
    codePrefix: 'NAV'
  },
  rateLimits: {
    apiRequestsPerMinute: 60,
    wellSearchesPerMinute: 30,
    navigationsPerHour: 100,
    hazardReportsPerDay: 10
  },
  endpoints: {
    occApiUrl: 'https://occ.ok.gov/api/v1',
    useProductionOcc: true,
    cacheTtlMinutes: 60
  }
};

const DEFAULT_FORCE_UPDATE: Omit<ForceUpdateConfig, 'updatedAt' | 'updatedBy'> = {
  enabled: false,
  android: {
    minVersionCode: 1,
    minVersionName: '1.0.0',
    storeUrl: 'https://play.google.com/store/apps/details?id=com.oilwellx.navigator',
    gracePeriodHours: 72,
    allowSkip: true
  },
  ios: {
    minBuildNumber: 1,
    minVersionString: '1.0.0',
    storeUrl: 'https://apps.apple.com/app/navigatorx/id0000000000',
    gracePeriodHours: 72,
    allowSkip: true
  },
  updateMessage: {
    title: 'Update Available',
    body: 'A new version of NavigatorX is available with important improvements and bug fixes.',
    whatsNew: []
  }
};

const DEFAULT_MAINTENANCE: Omit<MaintenanceConfig, 'updatedAt' | 'updatedBy'> = {
  enabled: false,
  message: {
    title: 'Scheduled Maintenance',
    body: 'NavigatorX is currently undergoing scheduled maintenance. We\'ll be back shortly.'
  },
  bypassUserIds: [],
  bypassEmailDomains: ['oilwellx.com', 'tge.la'],
  bypassSubscriptionStatuses: [],
  showCountdown: true,
  allowReadOnly: false
};

// ============================================================================
// HISTORY LOGGING
// ============================================================================

/**
 * Add an entry to the configuration history
 */
async function addConfigHistoryEntry(
  entry: Omit<ConfigHistoryEntry, 'id' | 'changedAt'>
): Promise<string> {
  const historyRef = collection(navDb, CONFIG_HISTORY_COLLECTION);
  const docRef = await addDoc(historyRef, {
    ...entry,
    changedAt: Timestamp.now()
  });
  return docRef.id;
}

/**
 * Get configuration history with optional filters
 */
export async function getConfigHistory(options?: {
  configType?: ConfigHistoryEntry['configType'];
  configKey?: string;
  changedBy?: string;
  limitCount?: number;
}): Promise<ConfigHistoryEntry[]> {
  let q = query(
    collection(navDb, CONFIG_HISTORY_COLLECTION),
    orderBy('changedAt', 'desc')
  );
  
  if (options?.limitCount) {
    q = query(q, limit(options.limitCount));
  }
  
  const snapshot = await getDocs(q);
  let results = snapshot.docs.map(doc => ({
    id: doc.id,
    ...doc.data()
  })) as ConfigHistoryEntry[];
  
  // Filter in memory for compound queries (Firestore limitation)
  if (options?.configType) {
    results = results.filter(r => r.configType === options.configType);
  }
  if (options?.configKey) {
    results = results.filter(r => r.configKey === options.configKey);
  }
  if (options?.changedBy) {
    results = results.filter(r => r.changedBy === options.changedBy);
  }
  
  return results;
}

// ============================================================================
// FEATURE FLAGS FUNCTIONS
// ============================================================================

/**
 * Get all feature flags
 */
export async function getFeatureFlags(): Promise<FeatureFlag[]> {
  const docRef = doc(navDb, CONFIG_COLLECTION, FEATURE_FLAGS_DOC);
  const docSnap = await getDoc(docRef);
  
  if (!docSnap.exists()) {
    return [];
  }
  
  const data = docSnap.data();
  return Object.values(data.flags || {}) as FeatureFlag[];
}

/**
 * Get a specific feature flag by key
 */
export async function getFeatureFlag(key: string): Promise<FeatureFlag | null> {
  const flags = await getFeatureFlags();
  return flags.find(f => f.key === key) || null;
}

/**
 * Create a new feature flag
 */
export async function createFeatureFlag(
  flag: Omit<FeatureFlag, 'createdAt' | 'updatedAt' | 'createdBy' | 'updatedBy'>,
  adminId: string
): Promise<void> {
  const docRef = doc(navDb, CONFIG_COLLECTION, FEATURE_FLAGS_DOC);
  const docSnap = await getDoc(docRef);
  
  const existingFlags = docSnap.exists() ? docSnap.data().flags || {} : {};
  
  if (existingFlags[flag.key]) {
    throw new Error(`Feature flag with key "${flag.key}" already exists`);
  }
  
  // Validate key format
  if (!/^[a-z][a-z0-9_]*$/.test(flag.key)) {
    throw new Error('Flag key must start with lowercase letter and contain only lowercase letters, numbers, and underscores');
  }
  
  const newFlag: FeatureFlag = {
    ...flag,
    createdAt: Timestamp.now(),
    updatedAt: Timestamp.now(),
    createdBy: adminId,
    updatedBy: adminId
  };
  
  await setDoc(docRef, {
    flags: {
      ...existingFlags,
      [flag.key]: newFlag
    }
  }, { merge: true });
  
  // Log to history
  await addConfigHistoryEntry({
    configType: 'feature_flag',
    configKey: flag.key,
    action: 'create',
    previousValue: null,
    newValue: newFlag,
    changedBy: adminId
  });
  
  await logAdminAction(adminId, 'CREATE_FEATURE_FLAG', flag.key, { flag: newFlag });
}

/**
 * Update an existing feature flag
 */
export async function updateFeatureFlag(
  key: string,
  updates: Partial<FeatureFlag>,
  adminId: string,
  reason?: string
): Promise<void> {
  const docRef = doc(navDb, CONFIG_COLLECTION, FEATURE_FLAGS_DOC);
  const docSnap = await getDoc(docRef);
  
  if (!docSnap.exists()) {
    throw new Error('Feature flags document not found');
  }
  
  const existingFlags = docSnap.data().flags || {};
  const existingFlag = existingFlags[key];
  
  if (!existingFlag) {
    throw new Error(`Feature flag "${key}" not found`);
  }
  
  const updatedFlag: FeatureFlag = {
    ...existingFlag,
    ...updates,
    key, // Ensure key cannot be changed
    createdAt: existingFlag.createdAt, // Preserve creation time
    createdBy: existingFlag.createdBy, // Preserve creator
    updatedAt: Timestamp.now(),
    updatedBy: adminId
  };
  
  await setDoc(docRef, {
    flags: {
      ...existingFlags,
      [key]: updatedFlag
    }
  }, { merge: true });
  
  // Log to history
  await addConfigHistoryEntry({
    configType: 'feature_flag',
    configKey: key,
    action: 'update',
    previousValue: existingFlag,
    newValue: updatedFlag,
    changedBy: adminId,
    reason
  });
  
  await logAdminAction(adminId, 'UPDATE_FEATURE_FLAG', key, { 
    previous: existingFlag, 
    updated: updatedFlag,
    reason 
  });
}

/**
 * Toggle a feature flag on/off
 */
export async function toggleFeatureFlag(
  key: string,
  enabled: boolean,
  adminId: string,
  reason?: string
): Promise<void> {
  const flag = await getFeatureFlag(key);
  
  if (flag?.isCritical && !enabled) {
    // Require explicit confirmation for disabling critical flags
    console.warn(`Disabling critical flag: ${key}`);
  }
  
  await updateFeatureFlag(key, { enabled }, adminId, reason);
  
  await logAdminAction(
    adminId, 
    enabled ? 'ENABLE_FEATURE_FLAG' : 'DISABLE_FEATURE_FLAG', 
    key, 
    { reason }
  );
}

/**
 * Update rollout percentage for a flag
 */
export async function updateFlagRollout(
  key: string,
  rolloutPercentage: number,
  adminId: string,
  reason?: string
): Promise<void> {
  if (rolloutPercentage < 0 || rolloutPercentage > 100) {
    throw new Error('Rollout percentage must be between 0 and 100');
  }
  
  await updateFeatureFlag(key, { rolloutPercentage }, adminId, reason);
  
  await logAdminAction(adminId, 'UPDATE_FLAG_ROLLOUT', key, { 
    rolloutPercentage,
    reason 
  });
}

/**
 * Add users to a flag's enabled list
 */
export async function addUsersToFlag(
  key: string,
  userIds: string[],
  adminId: string
): Promise<void> {
  const flag = await getFeatureFlag(key);
  if (!flag) throw new Error(`Flag "${key}" not found`);
  
  const enabledUserIds = [...new Set([...flag.enabledUserIds, ...userIds])];
  await updateFeatureFlag(key, { enabledUserIds }, adminId, `Added ${userIds.length} users`);
}

/**
 * Remove users from a flag's enabled list
 */
export async function removeUsersFromFlag(
  key: string,
  userIds: string[],
  adminId: string
): Promise<void> {
  const flag = await getFeatureFlag(key);
  if (!flag) throw new Error(`Flag "${key}" not found`);
  
  const enabledUserIds = flag.enabledUserIds.filter(id => !userIds.includes(id));
  await updateFeatureFlag(key, { enabledUserIds }, adminId, `Removed ${userIds.length} users`);
}

/**
 * Delete a feature flag
 */
export async function deleteFeatureFlag(key: string, adminId: string): Promise<void> {
  const docRef = doc(navDb, CONFIG_COLLECTION, FEATURE_FLAGS_DOC);
  const docSnap = await getDoc(docRef);
  
  if (!docSnap.exists()) {
    throw new Error('Feature flags document not found');
  }
  
  const existingFlags = docSnap.data().flags || {};
  const deletedFlag = existingFlags[key];
  
  if (!deletedFlag) {
    throw new Error(`Feature flag "${key}" not found`);
  }
  
  if (deletedFlag.isCritical) {
    throw new Error('Cannot delete critical feature flags. Mark as non-critical first.');
  }
  
  delete existingFlags[key];
  
  await setDoc(docRef, { flags: existingFlags });
  
  // Log to history
  await addConfigHistoryEntry({
    configType: 'feature_flag',
    configKey: key,
    action: 'delete',
    previousValue: deletedFlag,
    newValue: null,
    changedBy: adminId
  });
  
  await logAdminAction(adminId, 'DELETE_FEATURE_FLAG', key, { deletedFlag });
}

/**
 * Check if a feature flag is enabled for a specific user
 * This is the client-side evaluation function
 */
export function evaluateFeatureFlag(
  flag: FeatureFlag,
  userId: string,
  userSubscriptionStatus?: string,
  environment: string = 'production'
): boolean {
  // Check expiration
  if (flag.expiresAt && flag.expiresAt.toDate() < new Date()) {
    return false;
  }
  
  // Check environment
  if (flag.environment !== 'all' && flag.environment !== environment) {
    return false;
  }
  
  // Check if globally disabled
  if (!flag.enabled) {
    return false;
  }
  
  // Check if user is explicitly disabled
  if (flag.disabledUserIds.includes(userId)) {
    return false;
  }
  
  // Check if user is explicitly enabled
  if (flag.enabledUserIds.includes(userId)) {
    return true;
  }
  
  // Check subscription status targeting
  if (flag.enabledSubscriptionStatuses.length > 0 && userSubscriptionStatus) {
    if (!flag.enabledSubscriptionStatuses.includes(userSubscriptionStatus)) {
      return false;
    }
  }
  
  // Check rollout percentage
  if (flag.rolloutPercentage < 100) {
    // Use consistent hashing so user always gets same result
    const hash = simpleHash(userId + flag.key);
    const userPercentile = hash % 100;
    return userPercentile < flag.rolloutPercentage;
  }
  
  return true;
}

/**
 * Simple hash function for consistent rollout assignment
 */
function simpleHash(str: string): number {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash; // Convert to 32bit integer
  }
  return Math.abs(hash);
}

/**
 * Bulk evaluate all flags for a user
 */
export async function evaluateAllFlags(
  userId: string,
  userSubscriptionStatus?: string,
  environment: string = 'production'
): Promise<Record<string, boolean>> {
  const flags = await getFeatureFlags();
  const results: Record<string, boolean> = {};
  
  for (const flag of flags) {
    results[flag.key] = evaluateFeatureFlag(flag, userId, userSubscriptionStatus, environment);
  }
  
  return results;
}

// ============================================================================
// APP CONFIGURATION FUNCTIONS
// ============================================================================

/**
 * Get app configuration
 */
export async function getAppConfig(): Promise<AppConfig> {
  const docRef = doc(navDb, CONFIG_COLLECTION, APP_CONFIG_DOC);
  const docSnap = await getDoc(docRef);
  
  if (!docSnap.exists()) {
    // Return defaults if not configured
    return {
      ...DEFAULT_APP_CONFIG,
      updatedAt: Timestamp.now(),
      updatedBy: 'system'
    };
  }
  
  return docSnap.data() as AppConfig;
}

/**
 * Update app configuration
 */
export async function updateAppConfig(
  updates: Partial<Omit<AppConfig, 'updatedAt' | 'updatedBy'>>,
  adminId: string,
  reason?: string
): Promise<void> {
  const docRef = doc(navDb, CONFIG_COLLECTION, APP_CONFIG_DOC);
  const currentConfig = await getAppConfig();
  
  const newConfig: AppConfig = {
    ...currentConfig,
    ...updates,
    // Deep merge for nested objects
    trial: { ...currentConfig.trial, ...updates.trial },
    pricing: { ...currentConfig.pricing, ...updates.pricing },
    referral: { ...currentConfig.referral, ...updates.referral },
    rateLimits: { ...currentConfig.rateLimits, ...updates.rateLimits },
    endpoints: { ...currentConfig.endpoints, ...updates.endpoints },
    updatedAt: Timestamp.now(),
    updatedBy: adminId
  };
  
  await setDoc(docRef, newConfig);
  
  // Log to history
  await addConfigHistoryEntry({
    configType: 'app_config',
    action: 'update',
    previousValue: currentConfig,
    newValue: newConfig,
    changedBy: adminId,
    reason
  });
  
  await logAdminAction(adminId, 'UPDATE_APP_CONFIG', APP_CONFIG_DOC, { 
    changes: updates,
    reason 
  });
}

/**
 * Update trial configuration
 */
export async function updateTrialConfig(
  trialConfig: Partial<AppConfig['trial']>,
  adminId: string
): Promise<void> {
  await updateAppConfig({ trial: trialConfig as AppConfig['trial'] }, adminId, 'Trial config update');
}

/**
 * Update pricing configuration
 */
export async function updatePricingConfig(
  pricingConfig: Partial<AppConfig['pricing']>,
  adminId: string
): Promise<void> {
  await updateAppConfig({ pricing: pricingConfig as AppConfig['pricing'] }, adminId, 'Pricing config update');
}

/**
 * Update rate limits
 */
export async function updateRateLimits(
  rateLimits: Partial<AppConfig['rateLimits']>,
  adminId: string
): Promise<void> {
  await updateAppConfig({ rateLimits: rateLimits as AppConfig['rateLimits'] }, adminId, 'Rate limits update');
}

// ============================================================================
// FORCE UPDATE FUNCTIONS
// ============================================================================

/**
 * Get force update configuration
 */
export async function getForceUpdateConfig(): Promise<ForceUpdateConfig> {
  const docRef = doc(navDb, CONFIG_COLLECTION, FORCE_UPDATE_DOC);
  const docSnap = await getDoc(docRef);
  
  if (!docSnap.exists()) {
    return {
      ...DEFAULT_FORCE_UPDATE,
      updatedAt: Timestamp.now(),
      updatedBy: 'system'
    };
  }
  
  return docSnap.data() as ForceUpdateConfig;
}

/**
 * Update force update configuration
 */
export async function updateForceUpdateConfig(
  updates: Partial<Omit<ForceUpdateConfig, 'updatedAt' | 'updatedBy'>>,
  adminId: string,
  reason?: string
): Promise<void> {
  const docRef = doc(navDb, CONFIG_COLLECTION, FORCE_UPDATE_DOC);
  const currentConfig = await getForceUpdateConfig();
  
  const newConfig: ForceUpdateConfig = {
    ...currentConfig,
    ...updates,
    android: { ...currentConfig.android, ...updates.android },
    ios: { ...currentConfig.ios, ...updates.ios },
    updateMessage: { ...currentConfig.updateMessage, ...updates.updateMessage },
    updatedAt: Timestamp.now(),
    updatedBy: adminId
  };
  
  await setDoc(docRef, newConfig);
  
  // Log to history
  await addConfigHistoryEntry({
    configType: 'force_update',
    action: 'update',
    previousValue: currentConfig,
    newValue: newConfig,
    changedBy: adminId,
    reason
  });
  
  await logAdminAction(adminId, 'UPDATE_FORCE_UPDATE', FORCE_UPDATE_DOC, { 
    changes: updates,
    reason 
  });
}

/**
 * Enable force update with minimum version
 */
export async function enableForceUpdate(
  platform: 'android' | 'ios',
  minVersion: { code?: number; name: string },
  adminId: string,
  message?: { title?: string; body?: string; whatsNew?: string[] }
): Promise<void> {
  const updates: Partial<ForceUpdateConfig> = {
    enabled: true,
    updateMessage: message ? {
      title: message.title || 'Update Required',
      body: message.body || 'Please update to continue using NavigatorX.',
      whatsNew: message.whatsNew || []
    } : undefined
  };
  
  if (platform === 'android') {
    updates.android = {
      minVersionCode: minVersion.code || 1,
      minVersionName: minVersion.name,
      storeUrl: 'https://play.google.com/store/apps/details?id=com.oilwellx.navigator',
      gracePeriodHours: 72,
      allowSkip: false
    };
  } else {
    updates.ios = {
      minBuildNumber: minVersion.code || 1,
      minVersionString: minVersion.name,
      storeUrl: 'https://apps.apple.com/app/navigatorx/id0000000000',
      gracePeriodHours: 72,
      allowSkip: false
    };
  }
  
  await updateForceUpdateConfig(updates, adminId, `Force update enabled for ${platform} v${minVersion.name}`);
}

/**
 * Disable force update
 */
export async function disableForceUpdate(adminId: string): Promise<void> {
  await updateForceUpdateConfig({ enabled: false }, adminId, 'Force update disabled');
}

/**
 * Check if a version requires update
 */
export function checkVersionRequiresUpdate(
  config: ForceUpdateConfig,
  platform: 'android' | 'ios',
  currentVersionCode: number,
  currentVersionName: string
): { required: boolean; allowSkip: boolean; gracePeriodExpired: boolean } {
  if (!config.enabled) {
    return { required: false, allowSkip: true, gracePeriodExpired: false };
  }
  
  const platformConfig = platform === 'android' ? config.android : config.ios;
  const minVersionCode = platform === 'android' 
    ? platformConfig.minVersionCode 
    : (platformConfig as ForceUpdateConfig['ios']).minBuildNumber;
  
  if (currentVersionCode >= minVersionCode) {
    return { required: false, allowSkip: true, gracePeriodExpired: false };
  }
  
  // Calculate if grace period has expired
  const gracePeriodMs = platformConfig.gracePeriodHours * 60 * 60 * 1000;
  const updateTime = config.updatedAt.toDate().getTime();
  const gracePeriodExpired = Date.now() > (updateTime + gracePeriodMs);
  
  return {
    required: true,
    allowSkip: platformConfig.allowSkip && !gracePeriodExpired,
    gracePeriodExpired
  };
}

// ============================================================================
// MAINTENANCE MODE FUNCTIONS
// ============================================================================

/**
 * Get maintenance configuration
 */
export async function getMaintenanceConfig(): Promise<MaintenanceConfig> {
  const docRef = doc(navDb, CONFIG_COLLECTION, MAINTENANCE_DOC);
  const docSnap = await getDoc(docRef);
  
  if (!docSnap.exists()) {
    return {
      ...DEFAULT_MAINTENANCE,
      updatedAt: Timestamp.now(),
      updatedBy: 'system'
    };
  }
  
  return docSnap.data() as MaintenanceConfig;
}

/**
 * Update maintenance configuration
 */
export async function updateMaintenanceConfig(
  updates: Partial<Omit<MaintenanceConfig, 'updatedAt' | 'updatedBy'>>,
  adminId: string,
  reason?: string
): Promise<void> {
  const docRef = doc(navDb, CONFIG_COLLECTION, MAINTENANCE_DOC);
  const currentConfig = await getMaintenanceConfig();
  
  const newConfig: MaintenanceConfig = {
    ...currentConfig,
    ...updates,
    message: { ...currentConfig.message, ...updates.message },
    updatedAt: Timestamp.now(),
    updatedBy: adminId
  };
  
  // If enabling maintenance, set startedAt
  if (updates.enabled === true && !currentConfig.enabled) {
    newConfig.startedAt = Timestamp.now();
  }
  
  // If disabling maintenance, clear startedAt
  if (updates.enabled === false && currentConfig.enabled) {
    newConfig.startedAt = undefined;
  }
  
  await setDoc(docRef, newConfig);
  
  // Log to history
  await addConfigHistoryEntry({
    configType: 'maintenance',
    action: updates.enabled !== undefined 
      ? (updates.enabled ? 'enable' : 'disable') 
      : 'update',
    previousValue: currentConfig,
    newValue: newConfig,
    changedBy: adminId,
    reason
  });
  
  await logAdminAction(adminId, 'UPDATE_MAINTENANCE', MAINTENANCE_DOC, { 
    changes: updates,
    reason 
  });
}

/**
 * Enable maintenance mode
 */
export async function enableMaintenanceMode(
  adminId: string,
  options?: {
    message?: { title?: string; body?: string };
    estimatedDurationMinutes?: number;
    bypassUserIds?: string[];
    allowReadOnly?: boolean;
  }
): Promise<void> {
  const updates: Partial<MaintenanceConfig> = {
    enabled: true,
    showCountdown: true
  };
  
  if (options?.message) {
    updates.message = {
      title: options.message.title || 'Scheduled Maintenance',
      body: options.message.body || 'NavigatorX is undergoing maintenance. Please check back soon.'
    };
  }
  
  if (options?.estimatedDurationMinutes) {
    const endTime = new Date();
    endTime.setMinutes(endTime.getMinutes() + options.estimatedDurationMinutes);
    updates.estimatedEndTime = Timestamp.fromDate(endTime);
  }
  
  if (options?.bypassUserIds) {
    updates.bypassUserIds = options.bypassUserIds;
  }
  
  if (options?.allowReadOnly !== undefined) {
    updates.allowReadOnly = options.allowReadOnly;
  }
  
  await updateMaintenanceConfig(updates, adminId, 'Maintenance mode enabled');
}

/**
 * Disable maintenance mode
 */
export async function disableMaintenanceMode(adminId: string): Promise<void> {
  await updateMaintenanceConfig({
    enabled: false,
    startedAt: undefined,
    estimatedEndTime: undefined
  }, adminId, 'Maintenance mode disabled');
}

/**
 * Schedule maintenance mode
 */
export async function scheduleMaintenanceMode(
  adminId: string,
  scheduledStartTime: Date,
  estimatedDurationMinutes: number,
  message?: { title?: string; body?: string }
): Promise<void> {
  const endTime = new Date(scheduledStartTime);
  endTime.setMinutes(endTime.getMinutes() + estimatedDurationMinutes);
  
  await updateMaintenanceConfig({
    scheduledStartTime: Timestamp.fromDate(scheduledStartTime),
    estimatedEndTime: Timestamp.fromDate(endTime),
    message: message ? {
      title: message.title || 'Scheduled Maintenance',
      body: message.body || `Maintenance scheduled for ${scheduledStartTime.toLocaleString()}`
    } : undefined
  }, adminId, 'Maintenance scheduled');
}

/**
 * Add users to maintenance bypass list
 */
export async function addMaintenanceBypassUsers(
  userIds: string[],
  adminId: string
): Promise<void> {
  const config = await getMaintenanceConfig();
  const bypassUserIds = [...new Set([...config.bypassUserIds, ...userIds])];
  await updateMaintenanceConfig({ bypassUserIds }, adminId, `Added ${userIds.length} bypass users`);
}

/**
 * Remove users from maintenance bypass list
 */
export async function removeMaintenanceBypassUsers(
  userIds: string[],
  adminId: string
): Promise<void> {
  const config = await getMaintenanceConfig();
  const bypassUserIds = config.bypassUserIds.filter(id => !userIds.includes(id));
  await updateMaintenanceConfig({ bypassUserIds }, adminId, `Removed ${userIds.length} bypass users`);
}

/**
 * Check if a user can bypass maintenance
 */
export function canBypassMaintenance(
  config: MaintenanceConfig,
  userId: string,
  userEmail?: string,
  userSubscriptionStatus?: string
): boolean {
  if (!config.enabled) return true;
  
  // Check user ID bypass
  if (config.bypassUserIds.includes(userId)) return true;
  
  // Check email domain bypass
  if (userEmail && config.bypassEmailDomains.length > 0) {
    const emailDomain = userEmail.split('@')[1]?.toLowerCase();
    if (emailDomain && config.bypassEmailDomains.some(d => d.toLowerCase() === emailDomain)) {
      return true;
    }
  }
  
  // Check subscription status bypass
  if (userSubscriptionStatus && config.bypassSubscriptionStatuses.includes(userSubscriptionStatus)) {
    return true;
  }
  
  return false;
}

// ============================================================================
// ROLLBACK FUNCTIONS
// ============================================================================

/**
 * Rollback a configuration to a previous state
 */
export async function rollbackConfig(
  historyEntryId: string,
  adminId: string
): Promise<void> {
  const historyRef = doc(navDb, CONFIG_HISTORY_COLLECTION, historyEntryId);
  const historySnap = await getDoc(historyRef);
  
  if (!historySnap.exists()) {
    throw new Error('History entry not found');
  }
  
  const historyEntry = historySnap.data() as ConfigHistoryEntry;
  
  if (!historyEntry.previousValue) {
    throw new Error('Cannot rollback - no previous value stored');
  }
  
  // Restore the previous value based on config type
  switch (historyEntry.configType) {
    case 'feature_flag':
      if (historyEntry.configKey) {
        const docRef = doc(navDb, CONFIG_COLLECTION, FEATURE_FLAGS_DOC);
        const docSnap = await getDoc(docRef);
        const flags = docSnap.exists() ? docSnap.data().flags || {} : {};
        
        flags[historyEntry.configKey] = {
          ...historyEntry.previousValue,
          updatedAt: Timestamp.now(),
          updatedBy: adminId
        };
        
        await setDoc(docRef, { flags });
      }
      break;
      
    case 'app_config':
      await setDoc(doc(navDb, CONFIG_COLLECTION, APP_CONFIG_DOC), {
        ...historyEntry.previousValue,
        updatedAt: Timestamp.now(),
        updatedBy: adminId
      });
      break;
      
    case 'force_update':
      await setDoc(doc(navDb, CONFIG_COLLECTION, FORCE_UPDATE_DOC), {
        ...historyEntry.previousValue,
        updatedAt: Timestamp.now(),
        updatedBy: adminId
      });
      break;
      
    case 'maintenance':
      await setDoc(doc(navDb, CONFIG_COLLECTION, MAINTENANCE_DOC), {
        ...historyEntry.previousValue,
        updatedAt: Timestamp.now(),
        updatedBy: adminId
      });
      break;
  }
  
  // Log the rollback
  await addConfigHistoryEntry({
    configType: historyEntry.configType,
    configKey: historyEntry.configKey,
    action: 'rollback',
    previousValue: historyEntry.newValue,
    newValue: historyEntry.previousValue,
    changedBy: adminId,
    reason: `Rollback to state from ${historyEntry.changedAt.toDate().toISOString()}`
  });
  
  await logAdminAction(adminId, 'ROLLBACK_CONFIG', historyEntryId, {
    configType: historyEntry.configType,
    configKey: historyEntry.configKey
  });
}

// ============================================================================
// REAL-TIME LISTENERS
// ============================================================================

/**
 * Subscribe to feature flag changes
 */
export function subscribeToFeatureFlags(
  callback: (flags: FeatureFlag[]) => void
): () => void {
  const docRef = doc(navDb, CONFIG_COLLECTION, FEATURE_FLAGS_DOC);
  
  return onSnapshot(docRef, (snapshot) => {
    if (snapshot.exists()) {
      const data = snapshot.data();
      callback(Object.values(data.flags || {}) as FeatureFlag[]);
    } else {
      callback([]);
    }
  });
}

/**
 * Subscribe to maintenance mode changes
 */
export function subscribeToMaintenance(
  callback: (config: MaintenanceConfig) => void
): () => void {
  const docRef = doc(navDb, CONFIG_COLLECTION, MAINTENANCE_DOC);
  
  return onSnapshot(docRef, (snapshot) => {
    if (snapshot.exists()) {
      callback(snapshot.data() as MaintenanceConfig);
    } else {
      callback({
        ...DEFAULT_MAINTENANCE,
        updatedAt: Timestamp.now(),
        updatedBy: 'system'
      });
    }
  });
}

/**
 * Subscribe to force update changes
 */
export function subscribeToForceUpdate(
  callback: (config: ForceUpdateConfig) => void
): () => void {
  const docRef = doc(navDb, CONFIG_COLLECTION, FORCE_UPDATE_DOC);
  
  return onSnapshot(docRef, (snapshot) => {
    if (snapshot.exists()) {
      callback(snapshot.data() as ForceUpdateConfig);
    } else {
      callback({
        ...DEFAULT_FORCE_UPDATE,
        updatedAt: Timestamp.now(),
        updatedBy: 'system'
      });
    }
  });
}

// ============================================================================
// INITIALIZATION
// ============================================================================

/**
 * Initialize all configuration documents with defaults if they don't exist
 */
export async function initializeConfigs(adminId: string = 'system'): Promise<void> {
  const batch = writeBatch(navDb);
  
  // Check and initialize each config
  const appConfigRef = doc(navDb, CONFIG_COLLECTION, APP_CONFIG_DOC);
  const appConfigSnap = await getDoc(appConfigRef);
  if (!appConfigSnap.exists()) {
    batch.set(appConfigRef, {
      ...DEFAULT_APP_CONFIG,
      updatedAt: Timestamp.now(),
      updatedBy: adminId
    });
  }
  
  const forceUpdateRef = doc(navDb, CONFIG_COLLECTION, FORCE_UPDATE_DOC);
  const forceUpdateSnap = await getDoc(forceUpdateRef);
  if (!forceUpdateSnap.exists()) {
    batch.set(forceUpdateRef, {
      ...DEFAULT_FORCE_UPDATE,
      updatedAt: Timestamp.now(),
      updatedBy: adminId
    });
  }
  
  const maintenanceRef = doc(navDb, CONFIG_COLLECTION, MAINTENANCE_DOC);
  const maintenanceSnap = await getDoc(maintenanceRef);
  if (!maintenanceSnap.exists()) {
    batch.set(maintenanceRef, {
      ...DEFAULT_MAINTENANCE,
      updatedAt: Timestamp.now(),
      updatedBy: adminId
    });
  }
  
  const featureFlagsRef = doc(navDb, CONFIG_COLLECTION, FEATURE_FLAGS_DOC);
  const featureFlagsSnap = await getDoc(featureFlagsRef);
  if (!featureFlagsSnap.exists()) {
    batch.set(featureFlagsRef, { flags: {} });
  }
  
  await batch.commit();
}
